/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins } from '@blend/consumer-components';
import { SelectInput, colors } from '@blend/consumer-components';

import { useAudioInputDevices } from '../../../../hooks/useDevice';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { SELECTED_AUDIO_INPUT_KEY } from '../../../../definitions/devices';
import { VolumeMeter } from './VolumeMeter';

type SelectInputValue = any;

export namespace AudioInputDevice {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    name: string;
    sidebarView?: boolean;
    onChange(value: SelectInputValue | null): void;
  }
}

const styles = StyleSheet.create({
  sidebarViewWrapper: {
    ...mixins.margin(0, 0, 8, 0),
  },
  fullViewWrapper: {
    ...mixins.margin(0, 0, 16, 0),
  },
});

export const AudioInputDevice: React.FC<AudioInputDevice.Props> = ({ name, sidebarView = false, onChange }) => {
  const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);

  const { localTracks, videoProvider, getLocalAudioTrack } = useVideoContext();
  const audioInputDevices = useAudioInputDevices(localTracks);
  const [audioInputDeviceId, setAudioInputDeviceId] = useState<undefined | string>(selectedAudioDeviceId || undefined);
  const localAudioTrack = localTracks.find(track => track.kind === 'audio') as VideoProvider.ILocalAudioTrack;

  const replaceTrack = useCallback(
    newDeviceId => {
      setAudioInputDeviceId(newDeviceId);
      window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, newDeviceId);
      // switching microphone works differently in Zoom vs Twilio
      // in Twilio, since we have the raw audioTrack, we can just call track.restart() with the new deviceId
      // in Zoom, we need to first unpublish the existing audio track and publish a new one by calling getLocalAudioTrack()
      // Note:
      // getLocalAudioTrack() is also important for Zoom because the fn also sets the new audio track in the VideoContext state
      // without setting the new audio track in the state, VolumeMeter will still use the old audio track
      void videoProvider.streamSwitchMicrophone(newDeviceId, localAudioTrack, getLocalAudioTrack);
    },
    [localAudioTrack, videoProvider, getLocalAudioTrack],
  );

  const handleChange = useCallback(
    e => {
      const newDeviceId = e.target.value;
      replaceTrack(newDeviceId);
    },
    [localAudioTrack],
  );

  const choices = useMemo(
    () =>
      audioInputDevices.map(device => {
        return { value: device.deviceId, text: device.label };
      }),
    [audioInputDevices],
  );

  useEffect(() => {
    const [defaultAudioDevice] = audioInputDevices;
    if (!audioInputDeviceId && defaultAudioDevice) {
      window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, defaultAudioDevice.deviceId);
      setAudioInputDeviceId(defaultAudioDevice.deviceId);
    }
  }, [audioInputDevices]);

  useEffect(() => {
    onChange(audioInputDeviceId);
  }, [audioInputDeviceId]);

  return (
    <>
      <VolumeMeter
        audioTrack={!!audioInputDeviceId ? localAudioTrack : undefined}
        data-it="VolumeMeter"
        className={css(sidebarView ? styles.sidebarViewWrapper : styles.fullViewWrapper)}
      />
      <SelectInput
        value={audioInputDeviceId}
        id={name}
        choices={choices}
        onChange={handleChange}
        data-it="AudioInputSelector"
        color={colors.catalystLight40}
        aria-label={'Select microphone device'}
      />
    </>
  );
};
